import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import { MapPin, Briefcase, DollarSign, Clock, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const JOBS_PER_PAGE = 20;
const HARD_CAP = 250;

const Jobs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [positions, setPositions] = useState([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    employmentType: '',
    workplace: '',
    experienceLevel: ''
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchPositions();
  }, []);

  // Reset to page 1 whenever filters or search change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters.employmentType, filters.workplace, filters.experienceLevel]);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions`,
        { params: { limit: HARD_CAP, offset: 0 } }
      );

      if (response.data.success) {
        setPositions(response.data.data || []);
        setTotalAvailable(response.data.pagination?.total ?? (response.data.data || []).length);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPositions = useMemo(() => positions.filter(position => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = position.title.toLowerCase().includes(term) ||
                         position.company_name?.toLowerCase().includes(term);

    const matchesEmploymentType = !filters.employmentType || position.employment_type === filters.employmentType;
    const matchesWorkplace = !filters.workplace || position.workplace === filters.workplace;
    const matchesExperience = !filters.experienceLevel || position.experience_level === filters.experienceLevel;

    return matchesSearch && matchesEmploymentType && matchesWorkplace && matchesExperience;
  }), [positions, searchTerm, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredPositions.length / JOBS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * JOBS_PER_PAGE;
  const pagePositions = filteredPositions.slice(pageStart, pageStart + JOBS_PER_PAGE);

  const handlePositionClick = (positionId) => {
    navigate(`/jobs/${positionId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Available Positions</h1>
          <p className="text-gray-600 mt-2">Find your next opportunity</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search positions or companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Employment Type Filter */}
            <select
              value={filters.employmentType}
              onChange={(e) => setFilters({ ...filters, employmentType: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="freelance">Freelance</option>
            </select>

            {/* Workplace Filter */}
            <select
              value={filters.workplace}
              onChange={(e) => setFilters({ ...filters, workplace: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Locations</option>
              <option value="on-site">On-site</option>
              <option value="hybrid">Hybrid</option>
              <option value="remote">Remote</option>
            </select>
          </div>
        </div>

        {/* Positions List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading positions...</p>
          </div>
        ) : filteredPositions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No positions found</h3>
            <p className="text-gray-600">
              {searchTerm || filters.employmentType || filters.workplace
                ? 'Try adjusting your search or filters'
                : 'Check back later for new opportunities'}
            </p>
          </div>
        ) : (
          <>
            {/* Result count + cap notice */}
            <div className="flex items-center justify-between mb-3" data-testid="jobs-meta">
              <p className="text-sm text-gray-600">
                Showing <span className="font-medium text-gray-900">{pagePositions.length === 0 ? 0 : pageStart + 1}–{pageStart + pagePositions.length}</span> of {filteredPositions.length}
                {totalAvailable > HARD_CAP && filteredPositions.length === HARD_CAP && (
                  <span className="ml-2 text-amber-600">· showing first {HARD_CAP} of {totalAvailable} — narrow your search to see more</span>
                )}
              </p>
              {totalPages > 1 && (
                <p className="text-sm text-gray-500">Page {currentPage} of {totalPages}</p>
              )}
            </div>

          <div className="space-y-4">
            {pagePositions.map((position) => (
              <div
                key={position.id}
                onClick={() => handlePositionClick(position.id)}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 hover:border-blue-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Left Section - Job Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      {/* Company Logo Placeholder */}
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-6 h-6 text-white" />
                      </div>

                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-1">
                          {position.title}
                        </h3>
                        <p className="text-gray-600 mb-3">
                          {position.company_name || position.organizations?.name || 'Company Name'}
                        </p>

                        {/* Job Details */}
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{position.location || 'Location not specified'}</span>
                          </div>
                          
                          {position.employment_type && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span className="capitalize">{position.employment_type}</span>
                            </div>
                          )}

                          {position.workplace && (
                            <div className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              <span className="capitalize">{position.workplace}</span>
                            </div>
                          )}

                          {(position.salary_min || position.salary_max) && (
                            <div className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              <span>
                                {position.salary_currency || '€'} {position.salary_min?.toLocaleString()}
                                {position.salary_max && ` - ${position.salary_max.toLocaleString()}`}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Skills */}
                        {position.must_have_skills && position.must_have_skills.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {position.must_have_skills.slice(0, 5).map((skill, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                              >
                                {skill}
                              </span>
                            ))}
                            {position.must_have_skills.length > 5 && (
                              <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                                +{position.must_have_skills.length - 5} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Section - Action */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePositionClick(position.id);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </div>

                {/* Posted Date */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Posted {new Date(position.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8" data-testid="jobs-pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="jobs-prev-page"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
                .map((n, idx, arr) => (
                  <React.Fragment key={n}>
                    {idx > 0 && arr[idx - 1] !== n - 1 && <span className="text-gray-400 px-1">…</span>}
                    <button
                      onClick={() => setPage(n)}
                      className={`min-w-[34px] px-2 py-1.5 text-sm rounded-md border ${
                        n === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                      data-testid={`jobs-page-${n}`}
                    >
                      {n}
                    </button>
                  </React.Fragment>
                ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="jobs-next-page"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default Jobs;
